/**
 * Typed fetch wrapper for the monitor-api endpoints.
 *
 * `VITE_MONITOR_API_BASE_URL` is empty by default — when the SPA is served
 * from `apps/monitor-api/public` (production) the API lives on the same
 * origin, so a relative URL is correct. A non-empty value points at a remote
 * monitor-api for local development.
 */

const BASE_URL = import.meta.env.VITE_MONITOR_API_BASE_URL ?? "";

/**
 * Per-stage slice of `Counts`. Added by monitor CR-01.
 */
export interface PerStageCounts {
  stage_id: string;
  stage_name: string;
  currently_in: number;
  enter_count: number;
  exit_count: number;
  /** null when the stage has no declared capacity. */
  capacity: number | null;
}

export interface Counts {
  event_id: string;
  event_name: string;
  currently_in: number;
  enter_count: number;
  exit_count: number;
  total_scanned: number;
  registered: number;
  /**
   * Per-stage live counts. Empty array when the event has no stages.
   * Added by monitor CR-01; the event-level board renders these as a
   * compact strip beneath the secondary counters when non-empty.
   */
  per_stage: PerStageCounts[];
  timestamp: string;
}

/**
 * Response from `GET /m/v1/stages/{stageId}/counts` (monitor CR-01). Mirrors
 * a `PerStageCounts` entry plus parent-event metadata; deliberately omits
 * the CR-04 per-operator fields because the monitor is anonymous.
 */
export interface StageCounts {
  stage_id: string;
  stage_name: string;
  event_id: string;
  event_name: string;
  currently_in: number;
  enter_count: number;
  exit_count: number;
  capacity: number | null;
  timestamp: string;
}

export class CountsError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "CountsError";
    this.status = status;
    this.code = code;
  }
}

async function readEnvelope(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export async function getCounts(eventId: string, token: string): Promise<Counts> {
  const url = `${BASE_URL}/m/v1/counts/${encodeURIComponent(
    eventId
  )}?token=${encodeURIComponent(token)}`;
  let response: Response;
  try {
    response = await fetch(url, { cache: "no-store" });
  } catch {
    throw new CountsError(0, "NETWORK", "Could not reach the monitor.");
  }
  const payload = await readEnvelope(response);
  if (!response.ok) {
    const envelope = payload as { error?: string; code?: string } | null;
    throw new CountsError(
      response.status,
      envelope?.code ?? "ERROR",
      envelope?.error ?? "Request failed"
    );
  }
  return payload as Counts;
}

/**
 * Result of `POST /m/v1/snapshot/:eventId?token=…` (monitor CR-02).
 * Always 202-shaped on success — partial recipient failures surface as
 * `warning` rather than throwing. `recipients_count` is 0 when the event
 * has no snapshot recipients configured (a soft-warning state).
 */
export interface SnapshotResponse {
  recipients_count: number;
  message_ids: string[];
  warning?: string;
}

export async function requestSnapshot(
  eventId: string,
  token: string
): Promise<SnapshotResponse> {
  const url = `${BASE_URL}/m/v1/snapshot/${encodeURIComponent(
    eventId
  )}?token=${encodeURIComponent(token)}`;
  let response: Response;
  try {
    response = await fetch(url, { method: "POST", cache: "no-store" });
  } catch {
    throw new CountsError(0, "NETWORK", "Could not reach the monitor.");
  }
  const payload = await readEnvelope(response);
  if (!response.ok) {
    const envelope = payload as { error?: string; code?: string } | null;
    throw new CountsError(
      response.status,
      envelope?.code ?? "ERROR",
      envelope?.error ?? "Snapshot request failed"
    );
  }
  return payload as SnapshotResponse;
}

export async function requestStageSnapshot(
  stageId: string,
  token: string
): Promise<SnapshotResponse> {
  const url = `${BASE_URL}/m/v1/stages/${encodeURIComponent(
    stageId
  )}/snapshot?token=${encodeURIComponent(token)}`;
  let response: Response;
  try {
    response = await fetch(url, { method: "POST", cache: "no-store" });
  } catch {
    throw new CountsError(0, "NETWORK", "Could not reach the monitor.");
  }
  const payload = await readEnvelope(response);
  if (!response.ok) {
    const envelope = payload as { error?: string; code?: string } | null;
    throw new CountsError(
      response.status,
      envelope?.code ?? "ERROR",
      envelope?.error ?? "Snapshot request failed"
    );
  }
  return payload as SnapshotResponse;
}

/**
 * One row in the `GET /m/v1/in-room/:eventId` response (monitor CR-03).
 * Every identifying field the attendees table carries is surfaced so the
 * dialog's single search box can match against any of them.
 */
export interface InRoomAttendee {
  id: string;
  name: string;
  company: string | null;
  email: string | null;
  source: string;
  badge_id: string;
  meetpoint_participant_id: number | null;
  /** Group / category labels the attendee belongs to (scan CR-07). [] when none. */
  group_names: string[];
  last_enter_at: string;
}

export interface InRoomResponse {
  event_id: string;
  event_name: string;
  stage_id?: string;
  stage_name?: string;
  in_room: InRoomAttendee[];
  in_room_truncated: boolean;
  timestamp: string;
}

export async function getInRoom(
  eventId: string,
  token: string
): Promise<InRoomResponse> {
  const url = `${BASE_URL}/m/v1/in-room/${encodeURIComponent(
    eventId
  )}?token=${encodeURIComponent(token)}`;
  let response: Response;
  try {
    response = await fetch(url, { cache: "no-store" });
  } catch {
    throw new CountsError(0, "NETWORK", "Could not reach the monitor.");
  }
  const payload = await readEnvelope(response);
  if (!response.ok) {
    const envelope = payload as { error?: string; code?: string } | null;
    throw new CountsError(
      response.status,
      envelope?.code ?? "ERROR",
      envelope?.error ?? "Request failed"
    );
  }
  return payload as InRoomResponse;
}

export async function getStageInRoom(
  stageId: string,
  token: string
): Promise<InRoomResponse> {
  const url = `${BASE_URL}/m/v1/stages/${encodeURIComponent(
    stageId
  )}/in-room?token=${encodeURIComponent(token)}`;
  let response: Response;
  try {
    response = await fetch(url, { cache: "no-store" });
  } catch {
    throw new CountsError(0, "NETWORK", "Could not reach the monitor.");
  }
  const payload = await readEnvelope(response);
  if (!response.ok) {
    const envelope = payload as { error?: string; code?: string } | null;
    throw new CountsError(
      response.status,
      envelope?.code ?? "ERROR",
      envelope?.error ?? "Request failed"
    );
  }
  return payload as InRoomResponse;
}

/**
 * Fetches one stage's live counts (monitor CR-01). Same HMAC-URL pattern as
 * `getCounts`, but the token signs the stage_id with the parent event's
 * webhook_secret. 404 → stage doesn't exist; 401 → bad token.
 */
export async function getStageCounts(
  stageId: string,
  token: string
): Promise<StageCounts> {
  const url = `${BASE_URL}/m/v1/stages/${encodeURIComponent(
    stageId
  )}/counts?token=${encodeURIComponent(token)}`;
  let response: Response;
  try {
    response = await fetch(url, { cache: "no-store" });
  } catch {
    throw new CountsError(0, "NETWORK", "Could not reach the monitor.");
  }
  const payload = await readEnvelope(response);
  if (!response.ok) {
    const envelope = payload as { error?: string; code?: string } | null;
    throw new CountsError(
      response.status,
      envelope?.code ?? "ERROR",
      envelope?.error ?? "Request failed"
    );
  }
  return payload as StageCounts;
}
